import React from 'react';
import { renderSafeHtml } from '../../../utils/renderHtml';
import { Tooltip } from 'react-tooltip';
import { Info } from 'lucide-react';

interface SynonymsRadioFieldProps {
  label: string;
  preferredValue: string;
  synonyms: string[];
  onChange: (value: string | null) => void;
  isDisabled: boolean;
  tooltipText?: string;
  styles: Record<string, React.CSSProperties>;
  onFocus?: () => void;
  onBlur?: () => void;
  hasValue?: boolean;
  isFocused?: boolean;
  getFloatingLabelStyle?: (
    fieldName: string,
    hasValue: boolean
  ) => React.CSSProperties;
  getFloatingInputStyle?: (
    isTextarea: boolean,
    fieldName: string
  ) => React.CSSProperties;
}

const SynonymsRadioField: React.FC<SynonymsRadioFieldProps> = ({
  label,
  preferredValue,
  synonyms,
  onChange,
  isDisabled,
  tooltipText,
  styles,
  onFocus,
  onBlur,
  hasValue = false,
  isFocused = false,
  getFloatingLabelStyle,
  getFloatingInputStyle,
}) => {
  // Sort synonyms alphabetically
  const sortedSynonyms = [...(synonyms || [])].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

  const handlePreferredNameChange = (value: string) => {
    onChange(value || null);
  };

  const handleRadioClick = (synonym: string) => {
    if (isDisabled) return;

    // If clicking the already-selected radio, uncheck it
    if (preferredValue === synonym) {
      onChange(null);
    } else {
      onChange(synonym);
    }
  };

  // Show placeholder only when focused and empty (label is floating)
  const shouldShowPlaceholder = isFocused && !preferredValue;

  return (
    <>
      {/* Preferred Product Name Text Input */}
      <div style={styles.floatingFieldContainer}>
        <input
          id="preferred_product_name"
          type="text"
          value={preferredValue || ''}
          onChange={e => handlePreferredNameChange(e.target.value)}
          disabled={isDisabled}
          onFocus={onFocus}
          onBlur={onBlur}
          style={
            getFloatingInputStyle
              ? getFloatingInputStyle(false, 'preferred_product_name')
              : {}
          }
          placeholder={
            shouldShowPlaceholder ? 'Enter or select from synonyms below' : ' '
          }
        />
        <label
          htmlFor="preferred_product_name"
          style={
            getFloatingLabelStyle
              ? getFloatingLabelStyle(
                  'preferred_product_name',
                  hasValue || !!preferredValue
                )
              : {}
          }>
          {label}
        </label>
      </div>

      {/* Synonyms Radio List */}
      <div style={componentStyles.synonymsSection}>
        <div style={componentStyles.synonymsLabel}>
          Synonyms
          {tooltipText && (
            <>
              <span
                data-tooltip-id="synonyms-preferred-name-tooltip"
                data-tooltip-content={tooltipText}
                data-tooltip-place="top"
                style={componentStyles.tooltipIcon}>
                <Info size={16} />
              </span>
              <Tooltip
                style={componentStyles.tooltip}
                id="synonyms-preferred-name-tooltip"
              />
            </>
          )}
        </div>
        {sortedSynonyms.length === 0 ? (
          <div style={componentStyles.noSynonyms}>No synonyms available</div>
        ) : (
          <div style={componentStyles.synonymsContainer}>
            {sortedSynonyms.map((synonym, index) => {
              const isSelected = preferredValue === synonym;
              return (
                <div
                  key={index}
                  style={{
                    ...componentStyles.synonymRow,
                    cursor: isDisabled ? 'default' : 'pointer',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                  }}
                  onClick={() => handleRadioClick(synonym)}>
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => {}} // Handled by div onClick
                    disabled={isDisabled}
                    style={{
                      marginRight: '8px',
                      cursor: isDisabled ? 'default' : 'pointer',
                    }}
                  />
                  <span style={componentStyles.synonymText}>
                    {renderSafeHtml(synonym)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

const componentStyles: Record<string, React.CSSProperties> = {
  synonymsSection: {
    marginBottom: '16px',
  },
  tooltipIcon: {
    marginLeft: '8px',
    cursor: 'pointer',
    position: 'relative',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  tooltip: {
    zIndex: 20,
  },
  synonymsLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  noSynonyms: {
    fontSize: '13px',
    color: '#9ca3af',
    fontStyle: 'italic',
    padding: '4px 0',
  },
  synonymsContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '8px',
    backgroundColor: '#f9fafb',
  },
  synonymRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 4px',
    borderRadius: '3px',
  },
  synonymText: {
    fontSize: '14px',
    color: '#374151',
    wordBreak: 'break-word',
    flex: 1,
  },
};

export default SynonymsRadioField;
